import type { RouteObject } from "react-router-dom";

import {
  PERSONAL_SETTINGS_TITLE,
  PROJECT_LINKS,
  PROJECT_STEPS,
  WORKSPACE_SETTINGS_TITLE,
  type ProjectLinkId,
  type ProjectStepId,
} from "config/navigation";
import { SETTINGS_SECTIONS } from "pages/settings/Settings.sections";
import { WORKSPACE_SETTINGS_SECTIONS } from "pages/settings/workspace/WorkspaceSettings.sections";

/**
 * What a crumb stands for in the navigation config, so its dropdown can list
 * siblings by id. Matching on labels broke whenever a label changed.
 */
export type BreadcrumbNavKey =
  | { kind: "step"; stepId: ProjectStepId }
  | { kind: "step-item"; stepId: ProjectStepId; itemId: string }
  | { kind: "project-link"; linkId: ProjectLinkId }
  | { kind: "workspace-section"; sectionId: string }
  | { kind: "members-tab"; scope: "project" | "workspace"; tab: MembersTab };

export type MembersTab = "users" | "roles";

export interface BreadcrumbHandle {
  /** A label, or `project-name` / `workspace-name` resolved at render time. */
  crumb: string;
  nav?: BreadcrumbNavKey;
}

export const MEMBERS_TABS: ReadonlyArray<{ tab: MembersTab; label: string }> = [
  { tab: "users", label: "Users" },
  { tab: "roles", label: "Roles & Permissions" },
];

const handle = (crumb: string, nav?: BreadcrumbNavKey): { handle: BreadcrumbHandle } => ({
  handle: nav ? { crumb, nav } : { crumb },
});

const linkLabel = (linkId: ProjectLinkId): string =>
  PROJECT_LINKS.find((link) => link.id === linkId)?.label ?? "";

const workspaceSectionTitle = (id: string): string =>
  WORKSPACE_SETTINGS_SECTIONS.find((section) => section.id === id)?.title ?? id;

const personalSectionTitle = (id: string): string =>
  SETTINGS_SECTIONS.find((section) => section.id === id)?.title ?? id;

const membersTabRoutes = (scope: "project" | "workspace"): RouteObject[] =>
  MEMBERS_TABS.map(({ tab, label }) => ({
    path: tab,
    ...handle(label, { kind: "members-tab", scope, tab }),
  }));

/** Script pages that are not in the sidebar but sit under Script. */
const SCRIPT_EXTRA_ROUTES: RouteObject[] = [
  { path: "outline", ...handle("Outline") },
  { path: "snapshots/:snapshotId", ...handle("Snapshot") },
  { path: "compare/:snapshotId", ...handle("Compare") },
];

const projectStepRoutes: RouteObject[] = PROJECT_STEPS.map((step) => ({
  path: step.segment,
  ...handle(step.label, { kind: "step", stepId: step.id }),
  children: [
    ...step.items.map((item) => ({
      path: item.segment,
      ...handle(item.label, { kind: "step-item", stepId: step.id, itemId: item.id }),
    })),
    ...(step.id === "script" ? SCRIPT_EXTRA_ROUTES : []),
    ...(step.id === "characters"
      ? [
          {
            path: "actors/:actorId",
            ...handle("Cast & Actors", {
              kind: "step-item",
              stepId: "characters",
              itemId: "characters-actors",
            }),
          },
        ]
      : []),
  ],
}));

const projectSettingsRoutes: RouteObject = {
  // No crumb of its own: each page under it is a project link in the sidebar.
  path: "settings",
  children: [
    {
      path: "general",
      ...handle(linkLabel("project-settings"), {
        kind: "project-link",
        linkId: "project-settings",
      }),
    },
    {
      path: "danger",
      ...handle(linkLabel("project-settings"), {
        kind: "project-link",
        linkId: "project-settings",
      }),
      children: [{ index: true, ...handle("Danger zone") }],
    },
    {
      path: "members",
      ...handle(linkLabel("team-members"), { kind: "project-link", linkId: "team-members" }),
      children: membersTabRoutes("project"),
    },
    {
      path: "credits",
      ...handle(linkLabel("ai-credits"), { kind: "project-link", linkId: "ai-credits" }),
    },
  ],
};

const workspaceSection = (path: string, sectionId: string, children?: RouteObject[]) => ({
  path,
  ...handle(workspaceSectionTitle(sectionId), { kind: "workspace-section", sectionId }),
  ...(children ? { children } : {}),
});

export const breadcrumbRoutes: RouteObject[] = [
  {
    path: "/workspaces/:workspaceId",
    ...handle("workspace-name"),
    children: [
      {
        path: "projects/:projectId",
        ...handle("project-name"),
        children: [...projectStepRoutes, projectSettingsRoutes],
      },
      {
        path: "settings",
        ...handle(WORKSPACE_SETTINGS_TITLE),
        children: [
          workspaceSection("general", "general"),
          workspaceSection("account", "account"),
          workspaceSection("members", "members", membersTabRoutes("workspace")),
          workspaceSection("projects", "projects"),
          workspaceSection("activity", "activity"),
          workspaceSection("billing", "billing"),
          workspaceSection("api-keys", "api-keys"),
          workspaceSection("connections", "api-keys"),
          workspaceSection("sso", "sso"),
        ],
      },
    ],
  },
  {
    path: "/settings",
    ...handle(PERSONAL_SETTINGS_TITLE),
    children: [
      { path: "profile", ...handle(personalSectionTitle("profile")) },
      {
        path: "security",
        ...handle("Security"),
        children: [
          { path: "account", ...handle(personalSectionTitle("security")) },
          { path: "sessions", ...handle(personalSectionTitle("sessions")) },
          { path: "login-history", ...handle(personalSectionTitle("login-history")) },
        ],
      },
      { path: "billing", ...handle(personalSectionTitle("billing")) },
      { path: "referrals", ...handle(personalSectionTitle("referrals")) },
      { path: "notifications", ...handle(personalSectionTitle("notifications-list")) },
    ],
  },
];
